/**
 * Containment Hierarchy Utilities
 *
 * Helpers for the `contains` edge type — a passive, directed parent→child
 * relationship for hierarchical reporting (epic → story → task).
 *
 * Soft rules enforced here:
 *   1. Single-parent constraint: each item may have at most one `contains` parent.
 *   2. No cycles: a parent cannot also be a descendant of the same child.
 */

import type { ItemRelationshipEdge } from '../types/prioritization/itemRelationships';

/** Walk the `contains` tree downward from parentId, returning all descendant item IDs. */
export function getContainsChildIds(
  parentId: string,
  edges: ItemRelationshipEdge[],
  visited = new Set<string>(),
): string[] {
  if (visited.has(parentId)) return [];
  visited.add(parentId);
  const children = edges
    .filter((e) => e.type === 'contains' && e.sourceItemId === parentId && e.status !== 'dissolved')
    .map((e) => e.targetItemId);
  return [
    ...children,
    ...children.flatMap((childId) => getContainsChildIds(childId, edges, visited)),
  ];
}

/** Returns the direct parent of an item via a `contains` edge, or null if none. */
export function getContainsParentId(
  itemId: string,
  edges: ItemRelationshipEdge[],
): string | null {
  const parentEdge = edges.find(
    (e) => e.type === 'contains' && e.targetItemId === itemId && e.status !== 'dissolved',
  );
  return parentEdge?.sourceItemId ?? null;
}

/** Walk the `contains` tree upward from itemId, returning all ancestor item IDs (nearest first). */
export function getContainsAncestorIds(
  itemId: string,
  edges: ItemRelationshipEdge[],
  visited = new Set<string>(),
): string[] {
  if (visited.has(itemId)) return [];
  visited.add(itemId);
  const parentId = getContainsParentId(itemId, edges);
  if (!parentId) return [];
  return [parentId, ...getContainsAncestorIds(parentId, edges, visited)];
}

/**
 * Returns true if creating a `contains` edge from sourceId → targetId would
 * introduce a cycle in the containment hierarchy.
 */
export function wouldCreateContainsCycle(
  sourceId: string,
  targetId: string,
  edges: ItemRelationshipEdge[],
): boolean {
  if (sourceId === targetId) return true;
  return getContainsAncestorIds(sourceId, edges).includes(targetId);
}

/**
 * Returns true if the given item already has a `contains` parent,
 * violating the single-parent constraint.
 */
export function hasContainsParent(
  itemId: string,
  edges: ItemRelationshipEdge[],
): boolean {
  return getContainsParentId(itemId, edges) !== null;
}

export type ContainsEdgeViolation = 'SELF_CONTAINMENT' | 'SINGLE_PARENT' | 'CYCLE';

/**
 * Validates a proposed `contains` edge (source → target).
 * Returns a violation code or null if the edge is allowed.
 */
export function validateContainsEdge(
  sourceId: string,
  targetId: string,
  edges: ItemRelationshipEdge[],
): ContainsEdgeViolation | null {
  if (sourceId === targetId) return 'SELF_CONTAINMENT';
  if (hasContainsParent(targetId, edges)) return 'SINGLE_PARENT';
  if (wouldCreateContainsCycle(sourceId, targetId, edges)) return 'CYCLE';
  return null;
}

const CONTAINS_EDGE_VIOLATION_MESSAGES: Record<ContainsEdgeViolation, string> = {
  SELF_CONTAINMENT: 'An item cannot contain itself.',
  SINGLE_PARENT: 'This item already has a parent in the Contains hierarchy. Remove or re-home it first.',
  CYCLE: 'This link would create a cycle in the Contains hierarchy.',
};

export function containsEdgeValidationMessage(violation: ContainsEdgeViolation): string {
  return CONTAINS_EDGE_VIOLATION_MESSAGES[violation];
}

export type ContainsHierarchyViolation =
  | { kind: 'MULTIPLE_PARENTS'; itemId: string; parentIds: string[] }
  | { kind: 'CYCLE'; sourceItemId: string; targetItemId: string };

/**
 * Scans active `contains` edges for hierarchy integrity violations that should
 * not exist in persisted data (single-parent constraint and acyclic tree).
 */
export function findContainsHierarchyViolations(
  edges: ItemRelationshipEdge[],
): ContainsHierarchyViolation[] {
  const active = edges.filter((e) => e.type === 'contains' && e.status !== 'dissolved');
  const violations: ContainsHierarchyViolation[] = [];

  const parentsByChild = new Map<string, string[]>();
  for (const edge of active) {
    const parents = parentsByChild.get(edge.targetItemId) ?? [];
    parents.push(edge.sourceItemId);
    parentsByChild.set(edge.targetItemId, parents);
  }

  for (const [itemId, parentIds] of parentsByChild) {
    if (parentIds.length > 1) {
      violations.push({ kind: 'MULTIPLE_PARENTS', itemId, parentIds });
    }
  }

  const cycleKeys = new Set<string>();
  for (const edge of active) {
    if (!getContainsChildIds(edge.targetItemId, active).includes(edge.sourceItemId)) continue;
    const key = [edge.sourceItemId, edge.targetItemId].sort().join(':');
    if (cycleKeys.has(key)) continue;
    cycleKeys.add(key);
    violations.push({ kind: 'CYCLE', sourceItemId: edge.sourceItemId, targetItemId: edge.targetItemId });
  }

  return violations;
}
